// PEV — Suivi de vaccination (Tableau 14) : séances par semaine, stratégie (Fixe / Avancée / Mobile) et tranche d'âge ;
// synthèse par antigène, taux d'abandon DTC1 → DTC3 et courbe de couverture cumulée, tout en temps réel.
import { useState } from 'react';
import {
  PEV_ANTIGENES, PEV_ANTIGENE_LABELS, PEV_STRATEGIES, PEV_STRATEGIE_LABELS, PEV_TRANCHES, PEV_TRANCHE_LABELS, validerSeancePev,
} from '../../../../shared/business-logic/index.ts';
import type { PevSeance, PevStrategie, PevTranche, PevAntigene, PointCourbe, CategoriePeriodePev } from '../../../../shared/business-logic/index.ts';
import { useModules } from '../../hooks/useModules.ts';
import { EnteteModule, NombreInput, PasDePeriode, Message, Pastille, fmtNombre, nomMois } from '../../components/ui.tsx';
import type { Periode } from '../Accueil.tsx';
import PevIntrants from './PevIntrants.tsx';

const SEMAINES = ['S1', 'S2', 'S3', 'S4', 'S5'];
const MOIS_C = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const brouillon = (): PevSeance => ({ semaine: 'S1', dateSeance: null, strategie: 'fixe', tranche: '0-11', doses: {} });

/** Courbe cumulée en SVG (aucune dépendance) : axe X = mois, axe Y = enfants vaccinés cumulés. */
function Courbe({ points }: { points: PointCourbe[] }) {
  const W = 640, H = 260, G = 44, D = 12, B = 28, T = 12;
  const max = Math.max(10, ...points.map((p) => Math.max(p.cibleCumul, p.dtc1Cumul, p.dtc3Cumul)));
  const x = (i: number) => G + (i * (W - G - D)) / 11;
  const y = (v: number) => H - B - (v * (H - B - T)) / max;
  const saisis = points.filter((p) => p.saisi);
  const dernier = saisis.length ? saisis[saisis.length - 1].mois : 0;
  const chemin = (cle: 'dtc1Cumul' | 'dtc3Cumul' | 'cibleCumul', jusqua = 12) => points.slice(0, jusqua).map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[cle]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="courbe" role="img" aria-label="Courbe de couverture vaccinale cumulée DTC1, DTC3 et cible">
      {[0, 0.25, 0.5, 0.75, 1].map((f) => <g key={f}><line x1={G} x2={W - D} y1={y(max * f)} y2={y(max * f)} className="grille-h" /><text x={G - 6} y={y(max * f) + 4} textAnchor="end" className="axe">{Math.round(max * f)}</text></g>)}
      {MOIS_C.map((m, i) => <text key={i} x={x(i)} y={H - 8} textAnchor="middle" className="axe">{m}</text>)}
      <path d={chemin('cibleCumul')} className="l-cible" />
      {dernier > 0 && <path d={chemin('dtc1Cumul', dernier)} className="l-dtc1" />}
      {dernier > 0 && <path d={chemin('dtc3Cumul', dernier)} className="l-dtc3" />}
      {saisis.map((p) => <g key={p.mois}><circle cx={x(p.mois - 1)} cy={y(p.dtc1Cumul)} r="3.5" className="p-dtc1" /><circle cx={x(p.mois - 1)} cy={y(p.dtc3Cumul)} r="3.5" className="p-dtc3" /></g>)}
    </svg>
  );
}

const pct = (v: number | null) => (v === null ? '—' : `${v} %`);

const COULEUR_LIBELLE: Record<string, string> = { VERT: 'Vert', JAUNE: 'Jaune', ROUGE: 'Rouge', ROUGE_FONCE: 'Rouge foncé' };

/** Sous la courbe : période, TCV, TAB et petit badge « Catégorie X » (3 cellules alignées). */
function CategoriePeriode({ cat, tcv, tab, periodeTexte }: { cat: CategoriePeriodePev | null; tcv: number | null; tab: number | null; periodeTexte: string }) {
  return (
    <div className="pev-bas" aria-live="polite">
      <div className="pev-bas-periode">Période : <b>{periodeTexte}</b></div>
      <div className="pev-bas-grille">
        <div className="pev-bas-cellule">
          <span>Taux de couverture (TCV)</span>
          <b>{pct(tcv)}</b>
        </div>
        <div className="pev-bas-cellule">
          <span>Taux d'abandon (TAB)</span>
          <b>{pct(tab)}</b>
        </div>
        <div className="pev-bas-cellule">
          <span>Catégorie</span>
          <em className={`pev-badge ${cat ? `c${cat.categorie}` : 'aucune'}`} role="status" aria-label={cat ? `${cat.libelle}, ${COULEUR_LIBELLE[cat.couleur]}` : 'Catégorie non calculable'}>
            {cat ? `Catégorie ${cat.categorie}` : '—'}
          </em>
        </div>
      </div>
    </div>
  );
}

export default function Pev({ periode }: { periode: Periode | null }) {
  const m = useModules(periode);
  const [b, setB] = useState<PevSeance>(brouillon());
  const [cleEdition, setCleEdition] = useState<string | null>(null); // séance en cours de modification (null = nouvelle séance)
  if (!periode) return <PasDePeriode />;
  if (!m.pret) return <p>Chargement…</p>;
  const calc = m.calc;
  if (!calc) return <Message niveau="warn">{m.erreur ?? 'Données indisponibles.'}</Message>;
  const pev = calc.pev;
  const seances = m.saisies('PEV_SEANCE');
  const mensuelBrut = (m.saisies('PEV_MENSUEL')[0]?.donnees ?? { dtc1: null, dtc3: null }) as Record<string, number | null>;
  const vb = validerSeancePev(b);
  const combos = PEV_STRATEGIES.flatMap((st) => PEV_TRANCHES.map((tr) => ({ k: `${st}|${tr}`, l: `${PEV_STRATEGIE_LABELS[st]} ${PEV_TRANCHE_LABELS[tr]}` })));

  async function ajouter() {
    if (!vb.ok) return;
    const doses = Object.fromEntries(Object.entries(b.doses).filter(([, v]) => v)) as PevSeance['doses'];
    await m.ecrire('PEV_SEANCE', cleEdition ?? `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, { ...b, doses });
    if (cleEdition) { setCleEdition(null); setB(brouillon()); return; }
    setB({ ...brouillon(), semaine: b.semaine, strategie: b.strategie, tranche: b.tranche }); // on garde le contexte pour enchaîner les saisies
  }
  function modifier(cle: string, d: PevSeance) {
    setCleEdition(cle); setB({ ...d, doses: { ...d.doses } });
    document.getElementById('form-seance-pev')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function annulerEdition() { setCleEdition(null); setB(brouillon()); }
  async function majMensuel(champ: string, v: number | null) { await m.ecrire('PEV_MENSUEL', 'MOIS', { ...mensuelBrut, [champ]: v }); }

  return (
    <div>
      <EnteteModule titre="PEV — Suivi de vaccination" periode={periode} statut={periode.statut} lectureSeule={m.lectureSeule}
        aide="Saisissez chaque séance une fois : la synthèse, les couvertures, le taux d'abandon et la courbe se calculent seuls." />

      <section className="carte">
        <h2>Cibles</h2>
        <div className="champs">
          <label className="champ-num"><span>Population totale du CSB (année {periode.annee})</span>
            <NombreInput label="Population totale" valeur={m.popTotale} disabled={m.lectureSeule} onChange={(v) => m.majParametres({ popTotale: v })} /></label>
          <div className="resultats">
            <div><span>Cible mensuelle naissances vivantes (3,75 %)</span><b>{fmtNombre(pev.cibles.naissance)}</b></div>
            <div><span>Cible mensuelle 0-11 mois (3,58 %)</span><b>{fmtNombre(pev.cibles.zeroOnze)}</b></div>
          </div>
        </div>
        {m.popTotale === null && <Message niveau="info">Indiquez la population totale pour obtenir les couvertures et la courbe.</Message>}
      </section>

      {!m.lectureSeule && (
        <section className="carte" id="form-seance-pev">
          <h2>{cleEdition ? 'Modifier la séance' : 'Nouvelle séance de vaccination'}</h2>
          <div className="ligne-actions">
            <label>Semaine<select value={b.semaine} onChange={(e) => setB({ ...b, semaine: e.target.value })}>{SEMAINES.map((s) => <option key={s}>{s}</option>)}</select></label>
            <label>Date<input type="date" value={b.dateSeance ?? ''} onChange={(e) => setB({ ...b, dateSeance: e.target.value || null })} /></label>
            <fieldset><legend>Stratégie</legend><div className="choix">{PEV_STRATEGIES.map((s) => <label key={s} className={`radio ${b.strategie === s ? 'on' : ''}`}><input type="radio" name="strat" checked={b.strategie === s} onChange={() => setB({ ...b, strategie: s as PevStrategie })} />{PEV_STRATEGIE_LABELS[s]}</label>)}</div></fieldset>
            <fieldset><legend>Tranche d'âge</legend><div className="choix">{PEV_TRANCHES.map((t) => <label key={t} className={`radio ${b.tranche === t ? 'on' : ''}`}><input type="radio" name="tr" checked={b.tranche === t} onChange={() => setB({ ...b, tranche: t as PevTranche })} />{PEV_TRANCHE_LABELS[t]}</label>)}</div></fieldset>
          </div>
          <div className="champs">{PEV_ANTIGENES.map((a) => (
            <label key={a} className="champ-num"><span>{PEV_ANTIGENE_LABELS[a]}</span>
              <NombreInput label={`Doses ${PEV_ANTIGENE_LABELS[a]}`} valeur={b.doses[a as PevAntigene]} onChange={(v) => setB({ ...b, doses: { ...b.doses, [a]: v ?? undefined } })} /></label>
          ))}</div>
          {!vb.ok && Object.keys(b.doses).length > 0 && <Message niveau="warn">{vb.erreurs[0]}</Message>}
          <div className="ligne-actions">
            <button onClick={ajouter} disabled={!vb.ok}>{cleEdition ? '✔ Enregistrer les modifications' : '+ Ajouter la séance'}</button>
            {cleEdition && <button type="button" className="secondaire" onClick={annulerEdition}>Annuler</button>}
          </div>
        </section>
      )}

      <section className="carte">
        <h2>Séances du mois <Pastille>{seances.length}</Pastille></h2>
        <div className="defil">
          <table className="saisie compact">
            <thead><tr><th>Sem.</th><th>Date</th><th>Stratégie</th><th>Âge</th>{PEV_ANTIGENES.map((a) => <th key={a}>{PEV_ANTIGENE_LABELS[a]}</th>)}<th /></tr></thead>
            <tbody>
              {seances.map((l) => { const d = l.donnees as PevSeance; return (
                <tr key={l.cle}><td>{d.semaine}</td><td>{d.dateSeance ?? ''}</td><td>{PEV_STRATEGIE_LABELS[d.strategie]}</td><td>{PEV_TRANCHE_LABELS[d.tranche]}</td>
                  {PEV_ANTIGENES.map((a) => <td key={a}>{d.doses[a] ?? ''}</td>)}
                  <td>{!m.lectureSeule && <>
                    <button className="secondaire" title="Modifier la séance" onClick={() => modifier(l.cle, d)}>✏️ Modifier</button>{' '}
                    <button className="x" title="Supprimer la séance" onClick={() => { if (confirm('Supprimer cette séance ?')) { if (cleEdition === l.cle) annulerEdition(); void m.supprimer('PEV_SEANCE', l.cle); } }}>✕</button></>}</td></tr>); })}
              {seances.length === 0 && <tr><td colSpan={5 + PEV_ANTIGENES.length} className="vide">Aucune séance saisie ce mois.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="carte">
        <h2>Synthèse par antigène</h2>
        <div className="defil">
          <table className="saisie compact">
            <thead><tr><th className="fixe">Antigène</th><th>Total</th>{combos.map((c) => <th key={c.k}>{c.l}</th>)}<th>Cible mens.</th><th>Couverture</th></tr></thead>
            <tbody>{PEV_ANTIGENES.map((a) => (
              <tr key={a}><td className="fixe">{PEV_ANTIGENE_LABELS[a]}</td><td className="calcule">{pev.totaux[a]}</td>{combos.map((c) => <td key={c.k}>{pev.ventilation[a][c.k] || ''}</td>)}
                <td>{fmtNombre(pev.cibles.parAntigene[a])}</td><td className="calcule">{pev.couverture[a] === null ? '' : `${pev.couverture[a]} %`}</td></tr>))}</tbody>
          </table>
        </div>
      </section>

      <section className="carte">
        <h2>Suivi mensuel DTC-HepB-Hib et abandon</h2>
        <div className="champs">
          <label className="champ-num"><span>DTC1 {pev.dtcAutomatique.dtc1 && <em className="lien">🔗 repris des séances (VPO1)</em>}</span>
            <NombreInput label="DTC1 du mois" valeur={mensuelBrut.dtc1 ?? pev.mensuel?.dtc1 ?? null} classe={pev.dtcAutomatique.dtc1 ? 'lie' : ''} disabled={m.lectureSeule} onChange={(v) => majMensuel('dtc1', v)} /></label>
          <label className="champ-num"><span>DTC3 {pev.dtcAutomatique.dtc3 && <em className="lien">🔗 repris des séances (VPO3)</em>}</span>
            <NombreInput label="DTC3 du mois" valeur={mensuelBrut.dtc3 ?? pev.mensuel?.dtc3 ?? null} classe={pev.dtcAutomatique.dtc3 ? 'lie' : ''} disabled={m.lectureSeule} onChange={(v) => majMensuel('dtc3', v)} /></label>
          <div className={`resultats ${pev.abandonMois !== null && pev.abandonMois > 10 ? 'alerte' : ''}`}><div><span>Taux d'abandon DTC1 → DTC3 (mois)</span><b>{pct(pev.abandonMois)}</b></div></div>
        </div>
        {pev.anomalies.map((a: string) => <Message key={a} niveau="warn">{a}</Message>)}
        <h3>Courbe de couverture cumulée</h3>
        <Courbe points={pev.courbe} />
        <p className="legende"><span className="l1">● DTC1 cumulé</span> <span className="l3">● DTC3 cumulé</span> <span className="lc">― Cible cumulée</span></p>
        <CategoriePeriode cat={pev.categoriePeriode} tcv={pev.tcvDtc1Periode} tab={pev.abandonPeriode} periodeTexte={periode.mois === 1 ? `${nomMois(1)} ${periode.annee}` : `${nomMois(1)} à ${nomMois(periode.mois)} ${periode.annee}`} />
      </section>

      <PevIntrants m={m} />
    </div>
  );
}
